import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  Gem,
  Gift,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import HomeHero from "../home components/HomeHero";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "var(--user-maroon, #230000)";
const MAROON_DARK = "var(--user-maroon-deep, #130000)";
const GOLD = "var(--user-gold, #C99832)";
const GOLD_LIGHT = "var(--user-gold-soft, #E3BD5B)";
const CREAM = "var(--user-card, #F9F0E5)";
const TEXT_DARK = "var(--user-text, #381414)";

const fallbackAbout = {
  introTitle: "ABOUT US",
  introParagraphOne:
    "S. L. GOLD was established in 1980 by Late Shri Radhey Shyam Soni (Uklana Wale), a visionary entrepreneur with a deep passion for fine jewellery. With over four decades of industry expertise, he built a strong foundation based on trust, innovation, and customer-centric craftsmanship. His legacy continues to inspire our journey as we evolve with time while preserving our core values.",
  introParagraphTwo:
    "Under his leadership, S. L. GOLD earned the reputation of being a trusted name in customized gold jewellery, known for high-quality craftsmanship and strong relationships with customers. Today, we proudly carry forward his vision, bringing elegance, tradition, and modern design together under the leadership of Jayant Soni.",
  heroImage:
    "https://images.unsplash.com/photo-1603561596112-db1d2f27c2b5?q=85&w=1500&auto=format&fit=crop",
  visionTitle: "Our Vision",
  visionText:
    "To become a leading name in lightweight gold jewellery, offering timeless designs that resonate with diverse cultures and modern lifestyles across India and beyond.",
  missionTitle: "Our Mission",
  missionText:
    "To deliver superior quality gold jewellery with exceptional design, ensuring customer satisfaction through authenticity, innovation, and excellence in every creation.",
  offerTitle: "What We Offer",
  offerItems: [
    "Lightweight gold jewellery crafted with precision",
    "A collection of 300+ exclusive, trend-driven designs",
    "Certified quality and lifetime genuineness guarantee",
    "Bespoke pieces tailored to customer preferences",
    "Designs ideal for daily wear, festive occasions, and gifting",
  ],
  chooseTitle: "Why Choose Us",
  chooseItems: [
    "Legacy of 40+ Years",
    "Unmatched Design Innovation",
    "Client-Centric Approach",
    "Trusted Quality & Certification",
    "Commitment to Craftsmanship",
  ],
  quoteImage:
    "https://images.unsplash.com/photo-1611652022419-a9419f74343d?q=85&w=1300&auto=format&fit=crop",
  quoteTitleLineOne: "Crafted with trust.",
  quoteTitleLineTwo: "Cherished for generations.",
  quoteTextOne:
    "At S. L. GOLD, we don't just create jewellery - we craft stories, emotions, and timeless beauty.",
  quoteTextTwo:
    "Our dedication to excellence ensures that every piece reflects sophistication, tradition, and enduring value.",
  highlightLabels: [
    "40+ Years\nof Legacy",
    "Certified\nQuality",
    "300+ Exclusive\nDesigns",
    "Customer\nFirst",
    "Perfect for Every\nOccasion",
  ],
};

const highlightIcons = [Gem, ShieldCheck, Sparkles, Eye, Gift];

function renderLabel(label) {
  return String(label || "")
    .split("\n")
    .map((line, index, lines) => (
      <React.Fragment key={`${line}-${index}`}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
}

function normalizeAbout(data = {}) {
  const merged = { ...fallbackAbout, ...data };
  const listOrFallback = (value, fallback) =>
    Array.isArray(value) && value.length > 0 ? value : fallback;
  const valueOrFallback = (field) =>
    merged[field] === null || merged[field] === undefined || merged[field] === ""
      ? fallbackAbout[field]
      : merged[field];
  const highlightLabels = listOrFallback(
    merged.highlightLabels,
    fallbackAbout.highlightLabels
  );

  return {
    ...merged,
    introTitle: valueOrFallback("introTitle"),
    introParagraphOne: valueOrFallback("introParagraphOne"),
    introParagraphTwo: valueOrFallback("introParagraphTwo"),
    heroImage: valueOrFallback("heroImage"),
    visionTitle: valueOrFallback("visionTitle"),
    visionText: valueOrFallback("visionText"),
    missionTitle: valueOrFallback("missionTitle"),
    missionText: valueOrFallback("missionText"),
    offerTitle: valueOrFallback("offerTitle"),
    chooseTitle: valueOrFallback("chooseTitle"),
    quoteImage: valueOrFallback("quoteImage"),
    quoteTitleLineOne: valueOrFallback("quoteTitleLineOne"),
    quoteTitleLineTwo: valueOrFallback("quoteTitleLineTwo"),
    quoteTextOne: valueOrFallback("quoteTextOne"),
    quoteTextTwo: valueOrFallback("quoteTextTwo"),
    offerItems: listOrFallback(merged.offerItems, fallbackAbout.offerItems),
    chooseItems: listOrFallback(merged.chooseItems, fallbackAbout.chooseItems),
    highlightLabels,
    highlights: highlightLabels.map((label, index) => ({
      icon: highlightIcons[index] || Sparkles,
      desktopLabel: renderLabel(label),
      mobileLabel: renderLabel(label),
    })),
  };
}

function GoldDivider({ compact = false, dark = false }) {
  return (
    <div
      className={`gold-divider ${compact ? "gold-divider-compact" : ""} ${
        dark ? "gold-divider-dark" : ""
      }`}
      aria-hidden="true"
    >
      <span />
      <i />
      <span />
    </div>
  );
}

function SectionIcon({ children, small = false }) {
  return (
    <div className={`section-icon ${small ? "section-icon-small" : ""}`}>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="premium-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function DesktopAbout({ about }) {
  return (
    <div className="desktop-about">
      {/* About Us Banner Section */}
      <section className="desktop-hero">
        <div className="desktop-inner-border" />

        <div className="corner-ornament corner-top-left" />
        <div className="corner-ornament corner-top-right" />

        <div className="desktop-hero-copy">
          <div className="hero-heading reveal-up">
            <GoldDivider compact />

            <h1>{about.introTitle}</h1>

            <GoldDivider compact />
          </div>

          <div className="hero-paragraphs">
            <p className="reveal-left">
              {about.introParagraphOne}
            </p>

            <p className="reveal-left">
              {about.introParagraphTwo}
            </p>
          </div>
        </div>

        <div className="desktop-hero-visual reveal-right">
          <img
            src={about.heroImage}
            alt="Luxury gold bangles displayed on rich maroon fabric"
          />

          <div className="desktop-image-overlay" />
        </div>

        <div className="desktop-curve" />

        <div className="curve-emblem" aria-hidden="true">
          <Gem />
        </div>
      </section>

      {/* Mission, Vision, Offer, and Why Choose Us Section */}
      <section className="desktop-information">
        <article className="desktop-info-column reveal-card">
          <SectionIcon>
            <Eye />
          </SectionIcon>

          <h2>{about.visionTitle}</h2>

          <GoldDivider compact dark />

          <p>
            {about.visionText}
          </p>
        </article>

        <article className="desktop-info-column reveal-card">
          <SectionIcon>
            <Target />
          </SectionIcon>

          <h2>{about.missionTitle}</h2>

          <GoldDivider compact dark />

          <p>
            {about.missionText}
          </p>
        </article>

        <article className="desktop-info-column reveal-card">
          <SectionIcon>
            <Gem />
          </SectionIcon>

          <h2>{about.offerTitle}</h2>

          <GoldDivider compact dark />

          <BulletList items={about.offerItems} />
        </article>

        <article className="desktop-info-column reveal-card">
          <SectionIcon>
            <ShieldCheck />
          </SectionIcon>

          <h2>{about.chooseTitle}</h2>

          <GoldDivider compact dark />

          <BulletList items={about.chooseItems} />
        </article>
      </section>

      {/* Crafted With Trust Section */}
      <section className="desktop-quote">
        <div className="desktop-quote-image reveal-left">
          <img
            src={about.quoteImage}
            alt="Gold jewellery pendant displayed on maroon silk"
          />
          <div className="desktop-quote-image-overlay" />
        </div>

        <div className="desktop-quote-content reveal-right">
          <span className="quote-symbol quote-symbol-left">“</span>

          <h2>
            {about.quoteTitleLineOne}
            <br />
            {about.quoteTitleLineTwo}
          </h2>

          <GoldDivider compact />

          <p>{about.quoteTextOne}</p>

          <p>{about.quoteTextTwo}</p>

          <span className="quote-symbol quote-symbol-right">”</span>
        </div>
      </section>

      {/* About Highlights Section */}
      <section className="desktop-highlights">
        {about.highlights.map(({ icon: Icon, desktopLabel }, index) => (
          <article
            className="desktop-highlight reveal-highlight"
            key={index}
          >
            <div className="highlight-icon">
              <Icon />
            </div>

            <h3>{desktopLabel}</h3>
          </article>
        ))}

        <div className="highlight-bottom-divider">
          <span />
          <Gem />
          <span />
        </div>
      </section>
    </div>
  );
}

function MobileInfoCard({
  icon: Icon,
  title,
  children,
  className = "",
}) {
  return (
    <article className={`mobile-info-card mobile-reveal ${className}`}>
      <SectionIcon small>
        <Icon />
      </SectionIcon>

      <div className="mobile-card-copy">
        <h2>{title}</h2>
        <GoldDivider compact dark />
        {children}
      </div>
    </article>
  );
}

function MobileAbout({ about }) {
  return (
    <div className="mobile-about">
      {/* Mobile About Us Banner Section */}
      <section className="mobile-hero">
        <div className="mobile-inner-border" />

        <div className="mobile-heading mobile-reveal">
          <GoldDivider compact />
          <h1>{about.introTitle}</h1>
          <GoldDivider compact />
        </div>

        <div className="mobile-image mobile-reveal">
          <img
            src={about.heroImage}
            alt="Luxury gold bangles displayed on maroon fabric"
          />
          <div className="mobile-image-overlay" />
        </div>

        <div className="mobile-about-copy">
          <p className="mobile-reveal">
            {about.introParagraphOne}
          </p>

          <p className="mobile-reveal">
            {about.introParagraphTwo}
          </p>
        </div>

        <GoldDivider compact />
      </section>

      {/* Mobile Mission, Vision, Offer, and Why Choose Us Section */}
      <section className="mobile-information">
        <MobileInfoCard icon={Eye} title={about.visionTitle}>
          <p>
            {about.visionText}
          </p>
        </MobileInfoCard>

        <MobileInfoCard icon={Target} title={about.missionTitle}>
          <p>
            {about.missionText}
          </p>
        </MobileInfoCard>

        <MobileInfoCard icon={Gem} title={about.offerTitle}>
          <BulletList items={about.offerItems} />
        </MobileInfoCard>

        <MobileInfoCard icon={ShieldCheck} title={about.chooseTitle}>
          <BulletList items={about.chooseItems} />
        </MobileInfoCard>
      </section>

      {/* Mobile Crafted With Trust Section */}
      <section className="mobile-quote mobile-reveal">
        <div className="mobile-quote-heading">
          <span>“</span>

          <h2>
            {about.quoteTitleLineOne}
            <br />
            {about.quoteTitleLineTwo}
          </h2>

          <span>”</span>
        </div>

        <GoldDivider compact />

        <p>{about.quoteTextOne}</p>

        <p>{about.quoteTextTwo}</p>
      </section>

      {/* Mobile About Highlights Section */}
      <section className="mobile-highlights">
        {about.highlights.map(({ icon: Icon, mobileLabel }, index) => (
          <article
            className="mobile-highlight mobile-reveal"
            key={index}
          >
            <div className="mobile-highlight-icon">
              <Icon />
            </div>

            <h3>{mobileLabel}</h3>
          </article>
        ))}
      </section>
    </div>
  );
}

export default function AboutUs() {
  const rootRef = useRef(null);
  const [aboutData, setAboutData] = useState(fallbackAbout);
  const about = useMemo(() => normalizeAbout(aboutData), [aboutData]);

  useEffect(() => {
    let mounted = true;

    fetch(`${ApiURl}getAboutPage.php`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted || !response?.success || !response?.data) return;
        setAboutData(response.data);
      })
      .catch((error) => {
        console.error("Unable to load About Us settings:", error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useGSAP(
    () => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            ".reveal-up",
            ".reveal-left",
            ".reveal-right",
            ".reveal-card",
            ".reveal-highlight",
            ".mobile-reveal",
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
          }
        );

        return;
      }

      const desktopMatch = gsap.matchMedia();

      desktopMatch.add("(min-width: 769px)", () => {
        gsap.fromTo(
          ".hero-heading",
          {
            opacity: 0,
            y: 45,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1.15,
            ease: "power3.out",
            delay: 0.15,
          }
        );

        gsap.fromTo(
          ".desktop-hero-copy .reveal-left",
          {
            opacity: 0,
            x: -55,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1.05,
            stagger: 0.18,
            ease: "power3.out",
            delay: 0.35,
          }
        );

        gsap.fromTo(
          ".desktop-hero-visual",
          {
            opacity: 0,
            x: 80,
            scale: 1.05,
          },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 1.35,
            ease: "power3.out",
            delay: 0.25,
          }
        );

        gsap.utils.toArray(".reveal-card").forEach((card, index) => {
          gsap.fromTo(
            card,
            {
              opacity: 0,
              y: 65,
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.95,
              delay: index * 0.05,
              ease: "power3.out",
              scrollTrigger: {
                trigger: card,
                start: "top 88%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });

        gsap.fromTo(
          ".desktop-quote-image",
          {
            opacity: 0,
            x: -85,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".desktop-quote",
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.fromTo(
          ".desktop-quote-content",
          {
            opacity: 0,
            x: 85,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".desktop-quote",
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.fromTo(
          ".reveal-highlight",
          {
            opacity: 0,
            y: 38,
            scale: 0.94,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.75,
            stagger: 0.12,
            ease: "back.out(1.35)",
            scrollTrigger: {
              trigger: ".desktop-highlights",
              start: "top 86%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.to(".desktop-hero-visual img", {
          yPercent: 8,
          ease: "none",
          scrollTrigger: {
            trigger: ".desktop-hero",
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        });

        gsap.to(".desktop-quote-image img", {
          scale: 1.1,
          ease: "none",
          scrollTrigger: {
            trigger: ".desktop-quote",
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
          },
        });
      });

      desktopMatch.add("(max-width: 768px)", () => {
        gsap.utils.toArray(".mobile-reveal").forEach((element) => {
          gsap.fromTo(
            element,
            {
              opacity: 0,
              y: 34,
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.85,
              ease: "power3.out",
              scrollTrigger: {
                trigger: element,
                start: "top 90%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });

        gsap.to(".mobile-image img", {
          yPercent: 7,
          scale: 1.08,
          ease: "none",
          scrollTrigger: {
            trigger: ".mobile-hero",
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        });
      });

      const refreshTimer = window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 400);

      return () => {
        window.clearTimeout(refreshTimer);
        desktopMatch.revert();
      };
    },
    {
      scope: rootRef,
    }
  );

  return (
    <>
      <div className="about-homehero-laptop-offset">
        <HomeHero />
      </div>

      <main ref={rootRef} className="about-page">
        <div className="about-desktop-wrapper">
          <DesktopAbout about={about} />
        </div>

        <div className="about-mobile-wrapper">
          <MobileAbout about={about} />
        </div>

        <style>{`
          .about-page,
          .about-page * {
            box-sizing: border-box;
          }

          .about-page {
            --maroon: ${MAROON};
            --maroon-dark: ${MAROON_DARK};
            --gold: ${GOLD};
            --gold-light: ${GOLD_LIGHT};
            --cream: ${CREAM};
            --text-dark: ${TEXT_DARK};

            width: 100%;
            overflow: hidden;
            background: #e8d9d5;
            color: var(--cream);
            font-family: Georgia, "Times New Roman", serif;
          }

          .about-page img {
            display: block;
            width: 100%;
          }

          .about-mobile-wrapper {
            display: none;
          }

          /* Reduce only the laptop/desktop gap between Navbar and HomeHero */
          @media (min-width: 769px) {
            .about-homehero-laptop-offset {
              margin-top: -48px;
            }
          }

          .gold-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            width: min(340px, 100%);
            margin: 0 auto;
            color: var(--gold);
          }

          .gold-divider span {
            display: block;
            flex: 1;
            height: 1px;
            background: linear-gradient(
              90deg,
              transparent,
              currentColor 25%,
              currentColor 75%,
              transparent
            );
          }

          .gold-divider i {
            position: relative;
            display: block;
            width: 11px;
            height: 11px;
            border: 1px solid currentColor;
            transform: rotate(45deg);
          }

          .gold-divider i::before,
          .gold-divider i::after {
            content: "";
            position: absolute;
            top: 50%;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: currentColor;
            transform: translateY(-50%);
          }

          .gold-divider i::before {
            left: -12px;
          }

          .gold-divider i::after {
            right: -12px;
          }

          .gold-divider-compact {
            width: min(270px, 85%);
          }

          .gold-divider-dark {
            color: var(--gold);
          }

          .section-icon {
            display: grid;
            place-items: center;
            width: 82px;
            height: 82px;
            margin: 0 auto 18px;
            border: 2px solid var(--gold);
            border-radius: 50%;
            color: var(--gold-light);
            background:
              radial-gradient(
                circle at 30% 25%,
                rgba(104, 12, 13, 0.85),
                transparent 45%
              ),
              var(--maroon);
            box-shadow:
              inset 0 0 0 5px rgba(201, 152, 50, 0.08),
              0 12px 24px rgba(48, 5, 5, 0.18);
          }

          .section-icon svg {
            width: 39px;
            height: 39px;
            stroke-width: 1.35;
          }

          .section-icon-small {
            width: 61px;
            height: 61px;
            margin: 0;
            flex: 0 0 auto;
          }

          .section-icon-small svg {
            width: 29px;
            height: 29px;
          }

          .premium-list {
            margin: 0;
            padding: 0;
            list-style: none;
          }

          .premium-list li {
            position: relative;
            margin: 0 0 10px;
            padding-left: 18px;
          }

          .premium-list li::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0.63em;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: var(--gold);
            box-shadow: 0 0 0 2px rgba(201, 152, 50, 0.16);
          }

          /* ===========================
             DESKTOP VERSION
          =========================== */

          .desktop-about {
            width: 100%;
            margin: 0 0 44px;
            border: none; /* removed gold outer border/separator */
            background: var(--user-bg, #E8D9D5);
            box-shadow: 0 30px 80px rgba(48, 5, 5, 0.23);
          }

          .desktop-hero {
            position: relative;
            display: grid;
            grid-template-columns: 58% 42%;
            min-height: 520px;
            overflow: hidden;
            background:
              radial-gradient(
                circle at 86% 42%,
                rgba(255, 255, 255, 0.42),
                transparent 37%
              ),
              linear-gradient(115deg, var(--user-bg, #E8D9D5) 0%, var(--user-bg, #E8D9D5) 55%, var(--user-bg, #E8D9D5) 100%);
          }

          .desktop-inner-border {
            position: absolute;
            z-index: 10;
            inset: 13px;
            border: 1px solid rgba(201, 152, 50, 0.62);
            pointer-events: none;
          }

          .corner-ornament {
            position: absolute;
            z-index: 12;
            top: 4px;
            width: 34px;
            height: 34px;
            pointer-events: none;
          }

          .corner-ornament::before,
          .corner-ornament::after {
            content: "";
            position: absolute;
            border: 1px solid var(--gold);
            border-radius: 65% 0 65% 0;
          }

          .corner-ornament::before {
            width: 22px;
            height: 22px;
          }

          .corner-ornament::after {
            width: 13px;
            height: 13px;
            top: 10px;
            left: 10px;
          }

          .corner-top-left {
            left: 5px;
          }

          .corner-top-right {
            right: 5px;
            transform: scaleX(-1);
          }

          .desktop-hero-copy {
            position: relative;
            z-index: 5;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 50px 34px 88px 61px;
          }

          .hero-heading {
            margin-bottom: 38px;
          }

          .hero-heading h1 {
            margin: 18px 0;
            color: var(--maroon);
            font-size: clamp(46px, 4.8vw, 75px);
            font-weight: 400;
            line-height: 0.95;
            letter-spacing: 0.07em;
            text-align: center;
            text-shadow: none;
          }

          .hero-paragraphs {
            position: relative;
            z-index: 6;
            max-width: 730px;
          }

          .hero-paragraphs p {
            margin: 0 0 20px;
            color: #3b2624;
            font-family: Arial, Helvetica, sans-serif;
            font-size: clamp(14px, 1vw, 16px);
            line-height: 1.58;
          }

          .desktop-hero-visual {
            position: relative;
            min-height: 520px;
            overflow: hidden;
          }

          .desktop-hero-visual img {
            width: 100%;
            height: 112%;
            object-fit: cover;
            object-position: center;
            will-change: transform;
          }

          .desktop-image-overlay {
            position: absolute;
            inset: 0;
            background:
              linear-gradient(
                90deg,
                var(--user-bg, #E8D9D5) 0%,
                rgba(232, 217, 213, 0.78) 12%,
                rgba(232, 217, 213, 0.25) 45%,
                rgba(232, 217, 213, 0.06)
              ),
              linear-gradient(
                0deg,
                rgba(232, 217, 213, 0.8),
                transparent 50%
              );
          }

          .desktop-curve {
            position: absolute;
            z-index: 15;
            left: -4%;
            right: -4%;
            bottom: -74px;
            height: 140px;
            border-top: 4px solid var(--gold);
            border-radius: 50% 50% 0 0 / 100% 100% 0 0;
            background: var(--user-bg, #E8D9D5);
          }

          .curve-emblem {
            position: absolute;
            z-index: 17;
            left: 50%;
            bottom: 0;
            display: grid;
            place-items: center;
            width: 58px;
            height: 58px;
            color: var(--gold);
            background: var(--user-bg, #E8D9D5);
            transform: translateX(-50%);
          }

          .curve-emblem svg {
            width: 42px;
            height: 42px;
            stroke-width: 1.25;
          }

          .desktop-information {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            padding: 53px 19px 37px;
            color: var(--text-dark);
            background:
              radial-gradient(
                circle at 50% 0%,
                rgba(218, 188, 139, 0.18),
                transparent 43%
              ),
              var(--user-bg, #E8D9D5);
          }

          .desktop-info-column {
            position: relative;
            min-height: 336px;
            padding: 0 20px;
            text-align: center;
          }

          .desktop-info-column:not(:last-child) {
            border-right: 1px solid rgba(201, 152, 50, 0.35);
          }

          .desktop-info-column h2 {
            margin: 0;
            color: var(--text-dark);
            font-size: clamp(22px, 1.9vw, 31px);
            font-weight: 500;
            line-height: 1.1;
          }

          .desktop-info-column .gold-divider {
            margin-top: 11px;
            margin-bottom: 18px;
          }

          .desktop-info-column p,
          .desktop-info-column li {
            color: #392525;
            font-family: Arial, Helvetica, sans-serif;
            font-size: clamp(13px, 0.9vw, 15px);
            line-height: 1.52;
            text-align: left;
          }

          .desktop-info-column p {
            margin: 0;
          }

          .desktop-info-column .premium-list {
            text-align: left;
          }

          .desktop-quote {
            display: grid;
            grid-template-columns: 45% 55%;
            min-height: 292px;
            align-items: center;
            overflow: hidden;
            background:
              radial-gradient(
                circle at 75% 45%,
                rgba(255, 255, 255, 0.32),
                transparent 45%
              ),
              var(--user-bg, #E8D9D5);
          }

          .desktop-quote-image {
            position: relative;
            height: 250px;
            min-height: 0;
            margin: 28px 0 28px 56px;
            overflow: hidden;
            border: 1px solid rgba(201, 152, 50, 0.45);
          }

          .desktop-quote-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            will-change: transform;
          }

          .desktop-quote-image-overlay {
            position: absolute;
            inset: 0;
            background:
              linear-gradient(
                90deg,
                rgba(232, 217, 213, 0.08),
                rgba(232, 217, 213, 0.18) 55%,
                var(--user-bg, #E8D9D5)
              ),
              linear-gradient(
                0deg,
                rgba(232, 217, 213, 0.45),
                transparent 65%
              );
          }

          .desktop-quote-content {
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 36px 56px;
            text-align: center;
          }

          .desktop-quote-content h2 {
            margin: 0;
            color: var(--maroon);
            font-size: clamp(29px, 2.5vw, 44px);
            font-weight: 400;
            line-height: 1.23;
          }

          .desktop-quote-content .gold-divider {
            margin-top: 18px;
            margin-bottom: 18px;
          }

          .desktop-quote-content p {
            max-width: 760px;
            margin: 0 auto 9px;
            color: #3b2624;
            font-family: Arial, Helvetica, sans-serif;
            font-size: clamp(13px, 0.95vw, 16px);
            line-height: 1.42;
          }

          .quote-symbol {
            position: absolute;
            color: var(--gold-light);
            font-size: 59px;
            line-height: 1;
          }

          .quote-symbol-left {
            top: 30px;
            left: 48px;
          }

          .quote-symbol-right {
            right: 48px;
            bottom: 20px;
          }

          .desktop-highlights {
            position: relative;
            display: grid;
            grid-template-columns: repeat(5, minmax(0, 1fr));
            padding: 38px 27px 56px;
            color: var(--text-dark);
            background: var(--user-bg, #E8D9D5);
          }

          .desktop-highlight {
            min-height: 120px;
            padding: 0 14px;
            text-align: center;
          }

          .desktop-highlight:not(:nth-child(5)) {
            border-right: 1px solid rgba(201, 152, 50, 0.34);
          }

          .highlight-icon {
            display: grid;
            place-items: center;
            width: 56px;
            height: 56px;
            margin: 0 auto 14px;
            border: 1px solid rgba(201, 152, 50, 0.65);
            border-radius: 50%;
            color: var(--gold);
          }

          .highlight-icon svg {
            width: 31px;
            height: 31px;
            stroke-width: 1.25;
          }

          .desktop-highlight h3 {
            margin: 0;
            color: var(--text-dark);
            font-size: clamp(16px, 1.35vw, 22px);
            font-weight: 500;
            line-height: 1.25;
          }

          .highlight-bottom-divider {
            position: absolute;
            right: 25px;
            bottom: 20px;
            left: 25px;
            display: flex;
            align-items: center;
            gap: 14px;
            color: var(--gold);
          }

          .highlight-bottom-divider span {
            flex: 1;
            height: 1px;
            background: linear-gradient(
              90deg,
              transparent,
              currentColor 22%,
              currentColor 78%,
              transparent
            );
          }

          .highlight-bottom-divider svg {
            width: 34px;
            height: 34px;
            stroke-width: 1.25;
          }

          /* ===========================
             TABLET
          =========================== */

          @media (max-width: 1150px) and (min-width: 769px) {
            .desktop-about {
              width: 100%;
              margin-top: 0;
            }

            .desktop-hero {
              grid-template-columns: 56% 44%;
            }

            .desktop-hero-copy {
              padding: 44px 22px 88px 36px;
            }

            .hero-heading h1 {
              font-size: 46px;
            }

            .desktop-information {
              grid-template-columns: repeat(2, minmax(0, 1fr));
              padding: 46px 22px 28px;
            }

            .desktop-info-column {
              min-height: 308px;
              padding: 20px 30px;
            }

            .desktop-info-column:nth-child(2) {
              border-right: 0;
            }

            .desktop-info-column:nth-child(3),
            .desktop-info-column:nth-child(4) {
              border-top: 1px solid rgba(201, 152, 50, 0.35);
              padding-top: 34px;
            }

            .desktop-quote-content {
              padding: 32px 36px;
            }

            .desktop-highlights {
              padding-inline: 18px;
            }

            .desktop-highlight {
              padding-inline: 8px;
            }
          }

          /* ===========================
             MOBILE VERSION
          =========================== */

          @media (max-width: 768px) {
            .about-desktop-wrapper {
              display: none;
            }

            .about-mobile-wrapper {
              display: block;
            }

            .mobile-about {
              width: 100%;
              margin: 0 auto 34px;
              overflow: hidden;
              border: 1px solid rgba(201, 152, 50, 0.75);
              border-radius: 17px;
              background: var(--user-bg, #E8D9D5);
              box-shadow: 0 24px 60px rgba(48, 5, 5, 0.23);
            }

            .mobile-hero {
              position: relative;
              padding: 31px 18px 27px;
              overflow: hidden;
              color: var(--text-dark);
              background:
                radial-gradient(
                  circle at 80% 25%,
                  rgba(255, 255, 255, 0.46),
                  transparent 40%
                ),
                var(--user-bg, #E8D9D5);
            }

            .mobile-inner-border {
              position: absolute;
              inset: 7px;
              border: 1px solid rgba(201, 152, 50, 0.34);
              border-radius: 12px 12px 0 0;
              pointer-events: none;
            }

            .mobile-heading {
              position: relative;
              z-index: 3;
              margin-bottom: 21px;
            }

            .mobile-heading h1 {
              margin: 12px 0;
              color: var(--maroon);
              font-size: clamp(37px, 11vw, 49px);
              font-weight: 400;
              line-height: 1;
              letter-spacing: 0.05em;
              text-align: center;
            }

            .mobile-heading .gold-divider {
              width: 78%;
            }

            .mobile-image {
              position: relative;
              z-index: 3;
              height: 210px;
              margin: 0 4px 21px;
              overflow: hidden;
              border: 1px solid rgba(201, 152, 50, 0.5);
              border-radius: 2px;
            }

            .mobile-image img {
              width: 100%;
              height: 113%;
              object-fit: cover;
              object-position: center;
              will-change: transform;
            }

            .mobile-image-overlay {
              position: absolute;
              inset: 0;
              background:
                linear-gradient(
                  0deg,
                  rgba(232, 217, 213, 0.45),
                  transparent 50%
                ),
                linear-gradient(
                  90deg,
                  rgba(232, 217, 213, 0.2),
                  transparent 50%
                );
            }

            .mobile-about-copy {
              position: relative;
              z-index: 3;
            }

            .mobile-about-copy p {
              margin: 0 4px 17px;
              color: #3b2624;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 13px;
              line-height: 1.58;
              text-align: left;
            }

            .mobile-hero > .gold-divider {
              position: relative;
              z-index: 3;
              margin-top: 22px;
            }

            .mobile-information {
              padding: 10px 16px 12px;
              color: var(--text-dark);
              background: var(--user-bg, #E8D9D5);
            }

            .mobile-info-card {
              display: flex;
              align-items: flex-start;
              gap: 15px;
              padding: 20px 0;
            }

            .mobile-info-card:not(:last-child) {
              border-bottom: 1px solid rgba(201, 152, 50, 0.26);
            }

            .mobile-card-copy {
              min-width: 0;
              flex: 1;
            }

            .mobile-card-copy h2 {
              margin: 1px 0 0;
              color: var(--text-dark);
              font-size: 23px;
              font-weight: 500;
              line-height: 1.1;
            }

            .mobile-card-copy .gold-divider {
              width: 120px;
              margin: 8px 0 12px;
            }

            .mobile-card-copy p,
            .mobile-card-copy li {
              color: #3f2b2b;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12.5px;
              line-height: 1.5;
              text-align: left;
            }

            .mobile-card-copy p {
              margin: 0;
            }

            .mobile-card-copy .premium-list li {
              margin-bottom: 6px;
              padding-left: 14px;
            }

            .mobile-card-copy .premium-list li::before {
              top: 0.58em;
              width: 5px;
              height: 5px;
            }

            .mobile-quote {
              position: relative;
              padding: 27px 20px 25px;
              color: var(--text-dark);
              text-align: center;
              background:
                radial-gradient(
                  circle at 45% 15%,
                  rgba(255, 255, 255, 0.4),
                  transparent 44%
                ),
                var(--user-bg, #E8D9D5);
            }

            .mobile-quote-heading {
              display: grid;
              grid-template-columns: auto 1fr auto;
              align-items: center;
              gap: 8px;
            }

            .mobile-quote-heading span {
              color: var(--gold-light);
              font-size: 32px;
              line-height: 1;
            }

            .mobile-quote-heading h2 {
              margin: 0;
              color: var(--maroon);
              font-size: clamp(21px, 6vw, 27px);
              font-weight: 400;
              line-height: 1.24;
            }

            .mobile-quote .gold-divider {
              width: 65%;
              margin-top: 14px;
              margin-bottom: 14px;
            }

            .mobile-quote p {
              margin: 0 auto 8px;
              color: #3b2624;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12px;
              line-height: 1.5;
            }

            .mobile-highlights {
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 0;
              padding: 24px 11px 27px;
              color: var(--text-dark);
              background: var(--user-bg, #E8D9D5);
            }

            .mobile-highlight {
              min-width: 0;
              padding: 7px 4px 17px;
              text-align: center;
            }

            .mobile-highlight:nth-child(4),
            .mobile-highlight:nth-child(5) {
              grid-column: span 1;
            }

            .mobile-highlight:nth-child(4) {
              grid-column: 1 / 2;
              transform: translateX(50%);
            }

            .mobile-highlight:nth-child(5) {
              grid-column: 2 / 3;
              transform: translateX(50%);
            }

            .mobile-highlight-icon {
              display: grid;
              place-items: center;
              width: 49px;
              height: 49px;
              margin: 0 auto 9px;
              border: 1px solid rgba(201, 152, 50, 0.58);
              border-radius: 50%;
              color: var(--gold);
            }

            .mobile-highlight-icon svg {
              width: 27px;
              height: 27px;
              stroke-width: 1.25;
            }

            .mobile-highlight h3 {
              margin: 0;
              color: var(--text-dark);
              font-size: 11px;
              font-weight: 500;
              line-height: 1.25;
            }
          }

          /* REMOVE YELLOW DECORATIVE OUTER LINES FROM ABOUT HERO */
          .desktop-inner-border,
          .corner-ornament {
            display: none !important;
          }

          @media (max-width: 360px) {
            .mobile-about {
              width: 100%;
            }

            .mobile-hero {
              padding-inline: 14px;
            }

            .mobile-information {
              padding-inline: 12px;
            }

            .mobile-info-card {
              gap: 11px;
            }

            .section-icon-small {
              width: 53px;
              height: 53px;
            }

            .section-icon-small svg {
              width: 25px;
              height: 25px;
            }

            .mobile-card-copy h2 {
              font-size: 21px;
            }
          }
        `}</style>
      </main>
    </>
  );
}